import { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Spacing24 } from 'autogen/design-tokens/tokens';
import { OnGeoLocationCallbackContext, OnSearchCallbackContext } from 'components/storeList/shared/helpers';
import serializeForm from 'helpers/formatting';
import TextInput from 'components/shared/inputs/text';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import type { ISearch, SearchRequestBody } from '../shared/types';
import LocationSearch from './LocationSearch';
import Icon from 'components/Icon';
import { useIsMobile } from 'hooks/responsive';

// #region Styles
const Container = styled.div`
    margin-bottom: ${Spacing24};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
`;

const InputContainer = styled.div`
    display: grid;
`;
// #endregion Styles

export default function Search(props: ISearch) {
    const { translations, inputType = 'text', searchQuery, inputRef, trackingCategory } = props;
    const { placeholder, textFieldTitle, geoLocation } = translations;
    const [searchQueryValue, setSearchQueryValue] = useState<string | undefined>(searchQuery);
    const [submitValue, setSubmitValue] = useState<string | undefined>();
    const onSearchCallback = useContext(OnSearchCallbackContext);
    const onGeoLocationCallback = useContext(OnGeoLocationCallbackContext);
    const formRef = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (searchQuery) setSearchQueryValue(searchQuery);
    }, [searchQuery]);

    async function onSubmit(event) {
        event.preventDefault();
        const form = formRef.current;

        if (!form || searchQueryValue === submitValue) {
            return;
        }

        const data = serializeForm(form as HTMLFormElement) as SearchRequestBody;

        setSubmitValue(data.searchQuery);

        if (onSearchCallback) await onSearchCallback(data);

        if (trackingCategory) {
            UAEventTracking({ eventCategory: trackingCategory, eventAction: 'submitQuickViewSearch' });
        }
    }

    function handleSubmit(event) {
        event.preventDefault();
    }

    function onKeyDown(event) {
        if (event.key === 'Enter') {
            onSubmit(event);
        }
    }

    // used to hide keyboard when clicking "Search" on mobile
    function onKeyUp(event) {
        if (event.key === 'Enter' && isMobile) {
            event.target.blur();
        }
    }

    return (
        <Container>
            <Form ref={formRef} onSubmit={handleSubmit}>
                <InputContainer>
                    <TextInput
                        label={textFieldTitle}
                        ref={inputRef}
                        type={inputType}
                        name="searchQuery"
                        autoComplete="off"
                        placeholder={placeholder}
                        value={searchQueryValue}
                        required
                        onChange={(event) => {
                            setSearchQueryValue(event.target.value);
                        }}
                        onBlur={onSubmit}
                        onKeyDown={onKeyDown}
                        onKeyUp={onKeyUp}
                        enterKeyHint="search"
                        leftIcon={<Icon type="Search" size={16} />}
                        {...(geoLocation && onGeoLocationCallback
                            ? {
                                  rightIcon: (
                                      <LocationSearch translations={geoLocation} trackingCategory={trackingCategory} />
                                  )
                              }
                            : {})}
                    />
                </InputContainer>
            </Form>
        </Container>
    );
}
